import React, { useEffect, useState } from 'react';
import {
  Box, Button, IconButton, TextField, Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FormattedMessage } from 'react-intl';
import { DigitalSection, GetElementType } from '../../../redux/types';
import GenericDialog from '../../GenericDialog';
import { urlRegex } from '../../../utilities/regex';

type Props = {
  disable?: boolean;
  selectedSection: DigitalSection;
  updateDigitalSection: (section: DigitalSection) => void;
};

const SectionReferenceLinksEditor: React.FC<Props> = ({
  disable,
  selectedSection,
  updateDigitalSection,
}: Props) => {
  const [showReferenceLinkDialog, setShowReferenceLinkDialog] = useState<boolean>(false);
  const [referenceLinks, setReferenceLinks] = useState<DigitalSection['referenceLinks']>(selectedSection.referenceLinks);
  const [invalidUrl, setInvalidUrl] = useState<boolean[]>([]);

  useEffect(() => {
    if (selectedSection.referenceLinks && showReferenceLinkDialog === true) {
      setReferenceLinks(selectedSection.referenceLinks);
    } else {
      setReferenceLinks([{
        label: '',
        link: '',
      }]);
    }
  }, [selectedSection, showReferenceLinkDialog]);

  const invalidUrlExists = invalidUrl.includes(true);

  const onChangeReference = (
    txt: string,
    index: number,
    key: keyof GetElementType<DigitalSection['referenceLinks']>,
  ) => {
    if (key === 'link') {
      const urlPassed = urlRegex.test(txt);
      if (!urlPassed) {
        const newArr = [...invalidUrl];
        newArr[index] = true;
        setInvalidUrl(newArr);
      } else {
        setInvalidUrl([...invalidUrl].map((s, i) => (i === index ? false : s)));
      }
    }
    const newRef = referenceLinks?.map((r, i) => {
      if (index === i) {
        return {
          ...r,
          [key]: txt,
        };
      }
      return r;
    });
    setReferenceLinks(newRef);
  };

  const closeDialogHandler = () => {
    setShowReferenceLinkDialog(false);
    setReferenceLinks(undefined);
    setInvalidUrl([]);
  };

  const addNewReference = () => {
    setReferenceLinks(referenceLinks ? referenceLinks.concat({
      label: '', link: '',
    }) : [{ label: '', link: '' }]);
  };

  const saveReference = () => {
    if (invalidUrlExists) {
      return;
    }
    let cleanReferences = referenceLinks?.filter((r) => {
      const emptyValue = Object.values(r)
        .some((v) => v.trim().length === 0);
      return !emptyValue;
    });
    if (cleanReferences?.length === 0) {
      cleanReferences = undefined;
    } else {
      cleanReferences = cleanReferences?.map((item) => ({ ...item, link: item.link.trim() }));
    }
    updateDigitalSection({ ...selectedSection, referenceLinks: cleanReferences });
    closeDialogHandler();
  };

  const removeReferenceLink = (index: number) => {
    setInvalidUrl(invalidUrl.filter((s, i) => (i !== index)));
    setReferenceLinks(referenceLinks?.filter((l, i) => (i !== index)));
  };

  return (
    <Box>
      <Button onClick={() => setShowReferenceLinkDialog(true)} startIcon={<LinkIcon />} variant="outlined">reference link</Button>
      <GenericDialog
        isOpen={showReferenceLinkDialog}
        onRequestClose={closeDialogHandler}
      >
        <Box sx={{ m: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5">
              <FormattedMessage id="referencelink.title" />
            </Typography>
            <Typography>
              <FormattedMessage id="referenceLink.addText" />
              {' '}
              <Typography component="span" sx={{ fontWeight: 500 }}>
                {selectedSection?.name}
              </Typography>
            </Typography>
          </Box>
          <Box>
            {
                referenceLinks?.map((r, i) => (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      '& > :not(style)': { m: 1 },
                    }}
                  >
                    <TextField
                      size="small"
                      label="Reference Label"
                      fullWidth
                      value={referenceLinks[i].label}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onChangeReference(event.target.value, i, 'label');
                      }}
                      disabled={disable}
                    />
                    <TextField
                      size="small"
                      label="Reference Link"
                      fullWidth
                      value={referenceLinks[i].link}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onChangeReference(event.target.value, i, 'link');
                      }}
                      disabled={disable}
                      error={invalidUrl[i]}
                    />
                    <IconButton onClick={() => removeReferenceLink(i)} aria-label="delete-reference-link">
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                ))
              }
            <Box aria-label="add-button">
              <Button
                startIcon={<AddIcon />}
                disabled={disable}
                onClick={addNewReference}
              >
                Add New Reference
              </Button>
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} aria-label="footer">
            <Box>
              {
                invalidUrlExists && (
                  <Typography variant="body2" color="red">
                    <FormattedMessage id="referenceLink.urlError" />
                  </Typography>
                )
              }
            </Box>
            <Button disabled={disable || invalidUrlExists} onClick={saveReference} variant="contained"><FormattedMessage id="save" /></Button>
          </Box>
        </Box>
      </GenericDialog>
    </Box>
  );
};

export default SectionReferenceLinksEditor;
